import React from 'react';
import { CampaignStatus, CodeKind, CodeStatus } from '../api/types';
import { DateRangeControl, STICKY_FIRST_COL, defaultRange } from '@/components/shared';

// ─────────────────────────────────────────────────────────────────────────────
// Badges
// ─────────────────────────────────────────────────────────────────────────────

const BADGE = 'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border';

const KIND_STYLES: Record<CodeKind, string> = {
  personal: 'bg-gray-800 border-gray-700 text-gray-300',
  promoter: 'bg-violet-950/50 border-violet-800/60 text-violet-300',
  campaign: 'bg-blue-950/50 border-blue-800/60 text-blue-300',
};

export function CodeKindBadge({ kind }: { kind: CodeKind }) {
  return <span className={`${BADGE} ${KIND_STYLES[kind] ?? KIND_STYLES.personal}`}>{kind}</span>;
}

const CODE_STATUS_STYLES: Record<CodeStatus, string> = {
  active: 'bg-emerald-950/50 border-emerald-800/60 text-emerald-300',
  disabled: 'bg-gray-800 border-gray-700 text-gray-400',
};

export function CodeStatusBadge({ status }: { status: CodeStatus }) {
  return <span className={`${BADGE} ${CODE_STATUS_STYLES[status] ?? CODE_STATUS_STYLES.disabled}`}>{status}</span>;
}

const CAMPAIGN_STATUS_STYLES: Record<CampaignStatus, string> = {
  draft: 'bg-amber-950/50 border-amber-800/60 text-amber-300',
  active: 'bg-emerald-950/50 border-emerald-800/60 text-emerald-300',
  ended: 'bg-gray-800 border-gray-700 text-gray-400',
};

export function CampaignStatusBadge({ status }: { status: CampaignStatus }) {
  return (
    <span className={`${BADGE} ${CAMPAIGN_STATUS_STYLES[status] ?? CAMPAIGN_STATUS_STYLES.ended}`}>{status}</span>
  );
}

/** Marks the singleton fallback campaign every un-attached code lands on. */
export function FallbackDealBadge() {
  return <span className={`${BADGE} bg-indigo-950/50 border-indigo-800/60 text-indigo-300`}>fallback deal</span>;
}

export function ReviewFlagBadge({ flag }: { flag: string | null }) {
  if (!flag) return null;
  return <span className={`${BADGE} bg-red-950/50 border-red-800/60 text-red-300`}>{flag}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Date-range filter
// ─────────────────────────────────────────────────────────────────────────────

export interface DateRange {
  from: string;
  to: string;
}

/** The window the tab opens on: the same 30 days as every other analytics page. */
export function defaultDateRange(): DateRange {
  const [from, to] = defaultRange();
  return { from, to };
}

/**
 * The dashboard's date window: the shared analytics control, adapted to this
 * tab's `''`-means-unset `DateRange`. The backend reads `to` as
 * through-end-of-that-day, so `from=1 Jul&to=31 Jul` covers all of July.
 *
 * The window filters the STATS, not which codes exist — a code with no activity
 * in it still appears with zeros.
 */
export function DateRangeFilter({
  value,
  onChange,
  label = 'Window',
}: {
  value: DateRange;
  onChange: (r: DateRange) => void;
  label?: string;
}) {
  return (
    <DateRangeControl
      label={label}
      from={value.from || null}
      to={value.to || null}
      onChange={(from, to) => onChange({ from: from ?? '', to: to ?? '' })}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Table shell
// ─────────────────────────────────────────────────────────────────────────────

export function TableWrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto border border-gray-800 rounded-xl bg-gray-950">
      <table className={`w-full text-sm ${STICKY_FIRST_COL} [&_td:first-child]:bg-gray-950`}>{children}</table>
    </div>
  );
}

export const thClass = 'px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap';
export const thNumClass = `${thClass} text-right`;
export const tdClass = 'px-3 py-2.5 text-gray-300 align-top';
export const tdNumClass = `${tdClass} text-right tabular-nums`;

export function EmptyRow({ span, children }: { span: number; children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={span} className="px-3 py-8 text-center text-sm text-gray-500">
        {children}
      </td>
    </tr>
  );
}
